import React, {
	memo,
	useCallback,
	useMemo,
	useState
} from 'react';
import PropTypes from 'prop-types';
import {
	useHistory,
	useLocation
} from 'react-router';
import {
	Divider,
	Form,
	Tab
} from 'semantic-ui-react';
import {
	log,
	print
} from '../../../../../services/utils';

import { useAuthState } from '../../../../../contexts/AuthContext';
import { useAdminState } from '../../../../../contexts/AdminContext';

import Actions from '../../../../Shared/Actions';
import FormCheckbox from '../../../../Form/FormCheckbox';
import FormRadio from '../../../../Form/FormRadio';
import HelpModal from '../../../../Shared/HelpModal';

const NotificationsTab = ( {
	formData,
	formDataHasChanged,
	handleSave,
	resetFormData,
	updateField,
	validate
} ) => {
	const history = useHistory();
	const { state: navigationState } = useLocation();

	const { user: { permissions } } = useAuthState();
	const { notificationTypes, notificationDigestTypes } = useAdminState();

	const [ editable, setEditable ] = useState( !formData.id );
	const [ isSaving, setIsSaving ] = useState( false );

	const save = useCallback( async () => {
		const cb = () => {
			if ( !validate() ) {
				setEditable( false );
			}
		};

		setIsSaving( true );
		await handleSave( cb );
		setIsSaving( false );
	}, [
		handleSave,
		validate
	] );

	const saveAction = useMemo(
		() => ( {
			action: save,
			disable: !formDataHasChanged || isSaving,
			icon: 'save',
			permission: true,
			text: formData.id === 0
				? 'Save As New User'
				: 'Save'
		} ),
		[
			formData.id,
			formDataHasChanged,
			isSaving,
			save
		]
	);

	const cancel = useCallback( () => {
		resetFormData();

		if ( formData.id === 0 ) {
			if ( navigationState?.to ) {
				history.push( navigationState.to );
			} else {
				history.push( '/administration/users' );
			}
		} else {
			setEditable( false );
		}
	}, [
		formData.id,
		history,
		navigationState,
		resetFormData
	] );

	const cancelAction = useMemo(
		() => ( {
			action: cancel,
			icon: 'times',
			permission: true,
			text: 'Cancel'
		} ),
		[ cancel ]
	);

	const edit = useCallback( () => {
		setEditable( true );
	}, [] );

	const editAction = useMemo( () => ( {
		action: edit,
		disable: !permissions.editUsers.canExecute,
		icon: 'pencil',
		permission: permissions.editUsers.isAvailable,
		text: 'Edit'
	} ), [
		edit,
		permissions
	] );

	const printAction = useMemo( () => ( {
		action: print,
		disable: validate(),
		icon: 'print',
		permission: true,
		text: 'Print'
	} ), [ validate ] );

	const actions = useMemo(
		() => editable
			? [
				cancelAction,
				saveAction
			]
			: [
				printAction,
				editAction
			],
		[
			cancelAction,
			editable,
			saveAction,
			printAction,
			editAction
		]
	);

	// notificationTypes
	const renderNotificationEventControls = useCallback(
		() => {
			const arr = !editable
				? notificationTypes.filter( type => formData[ `event-${ type.id }` ] ).map( type => <p style={{ margin: '0.5em 0', height: '17px' }} key={ type.id }>{ type.name }</p> )
				: notificationTypes.map( type => (
					<FormCheckbox
						fieldLabel={ type.name }
						inline
						key={ type.id }
						name={ `event-${ type.id }` }
						onChange={ updateField }
						checked={ formData[ `event-${ type.id }` ] }
					/>
				) );

			return arr.length ? arr : <p style={{ margin: '0.5em 0', height: '17px' }}>None</p>;
		},
		[
			editable,
			formData,
			notificationTypes,
			updateField
		]
	);

	return (
		<Tab.Pane as="div">
			{ log( 'NotificationsTab' ) }
			<Actions actions={ actions } />
			<div className="contact-form-container">
				<Form autoComplete="off">
					<Form.Group>
						<FormRadio
							fieldLabel="Notification Digest"
							helpId={ editable ? 33 : null }
							name="notificationDigestType"
							onChange={ updateField }
							options={ notificationDigestTypes }
							readOnly={ !editable }
							required
							value={ formData.notificationDigestType }
							width={ 4 }
						/>
					</Form.Group>

					<Divider
						hidden
						style={ { margin: '1.5rem 0' } }
					/>

					<Form.Group grouped>
						<label>
							{ editable
								? <HelpModal
									id={ 34 }
									triggerPosition="left"
									triggerSize="small"
								/>
								: null
							}
								Notification Events
						</label>
						{ renderNotificationEventControls() }
					</Form.Group>
				</Form>
			</div>
		</Tab.Pane>
	);
};

NotificationsTab.propTypes = {
	errors: PropTypes.object,
	forceValidation: PropTypes.bool,
	formData: PropTypes.object,
	formDataHasChanged: PropTypes.bool,
	handleSave: PropTypes.func,
	resetFormData: PropTypes.func,
	updateField: PropTypes.func,
	validate: PropTypes.func
};

export default memo( NotificationsTab );
